import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CustomNavbar from '../CustomPermissions/CustomNavbar';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const MultiSelectDropdown = ({ options, selectedValues, onChange, placeholder, className }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = React.useRef(null);
    const selectedArray = selectedValues ? selectedValues.split(',').filter(Boolean) : [];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleOption = (value) => {
        let newValues;
        if (selectedArray.includes(value)) {
            newValues = selectedArray.filter(v => v !== value);
        } else {
            newValues = [...selectedArray, value];
        }
        onChange(newValues.join(','));
    };

    return (
        <div className={`relative ${className || ''}`} ref={containerRef}>
            <div 
                className="w-full px-3 py-1.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer bg-white flex justify-between items-center"
                onClick={() => setIsOpen(!isOpen)}
                style={{ minHeight: '34px' }}
            >
                <div className="truncate pr-2 text-sm">
                    {selectedArray.length > 0 ? selectedArray.join(', ') : <span className="text-gray-400">{placeholder}</span>}
                </div>
                <svg className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </div>
            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    {options.map(option => (
                        <div 
                            key={option.value} 
                            className="px-3 py-2 cursor-pointer hover:bg-gray-100 flex items-center text-sm text-gray-700"
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleOption(option.value);
                            }}
                        >
                            <input 
                                type="checkbox" 
                                className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                checked={selectedArray.includes(option.value)}
                                readOnly
                            />
                            {option.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
const MarketingLeads = () => {
    const [filteredLeads, setFilteredLeads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchNumber, setSearchNumber] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [searchHospital, setSearchHospital] = useState('');
    const [searchSource, setSearchSource] = useState('');
    const [searchCity, setSearchCity] = useState('');
    const [searchState, setSearchState] = useState('');
    const [searchCreatedBy, setSearchCreatedBy] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [followupByFilter, setFollowupByFilter] = useState('');

    const [qualityFilter, setQualityFilter] = useState('');
    const [searchReason, setSearchReason] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingLead, setEditingLead] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [leadToDelete, setLeadToDelete] = useState(null);
    const [callbackDoneFilter, setCallbackDoneFilter] = useState('');
    const [canEdit, setCanEdit] = useState(false);
    const [expandedLeadId, setExpandedLeadId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [todayCallbacks, setTodayCallbacks] = useState([]);
    const [isShowingTodayCallbacks, setIsShowingTodayCallbacks] = useState(false);
    const [creators, setCreators] = useState([]);
    const [isSuperuser] = useState(localStorage.getItem('is_superuser') === 'true');
    const [stats, setStats] = useState(null);

    const [pageSize, setPageSize] = useState(100);

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://lbbackend.codingindia.co.in';

    useEffect(() => {
        checkCallbacksToday();
        // Check for callbacks every hour
        const interval = setInterval(checkCallbacksToday, 3600000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Fetch unique creators for the Created By dropdown
        const fetchCreators = async () => {
            try {
                const token = localStorage.getItem('access_token');
                const response = await axios.get(`${API_BASE_URL}/MarketingLeads/creators/`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setCreators(response.data.creators || []);
            } catch (error) {
                console.error('Failed to fetch creators:', error);
            }
        };
        fetchCreators();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isSuperuser) return;
        const fetchStats = async () => {
            try {
                const token = localStorage.getItem('access_token');
                const response = await axios.get(`${API_BASE_URL}/MarketingLeads/stats/`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setStats(response.data);
            } catch (error) {
                console.error('Failed to fetch stats:', error);
            }
        };
        fetchStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuperuser]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchLeads(currentPage, pageSize);
        }, 500);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchNumber, statusFilter, searchHospital, searchSource, searchCity, searchState, searchCreatedBy, qualityFilter, searchReason, dateFrom, dateTo, callbackDoneFilter, followupByFilter, currentPage, pageSize]);

    const fetchLeads = async (page = currentPage, limit = pageSize) => {
        setLoading(true);
        setIsShowingTodayCallbacks(false);
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get(`${API_BASE_URL}/MarketingLeads/`, {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                    page: page,
                    page_size: limit,
                    search_number: searchNumber,
                    status: statusFilter,
                    hospital: searchHospital,
                    source: searchSource,
                    city: searchCity,
                    state: searchState,
                    created_by: searchCreatedBy,
                    quality: qualityFilter,
                    reason: searchReason,
                    date_from: dateFrom,
                    date_to: dateTo,
                    callback_done: callbackDoneFilter,
                    followup_by: followupByFilter,
                }
            });
            const leadsData = Array.isArray(response.data.data) ? response.data.data : [];
            setFilteredLeads(leadsData);
            setCanEdit(Boolean(response.data.can_edit));
            setTotalPages(response.data.total_pages || 1);
            setTotalItems(response.data.count || 0);
        } catch (error) {
            toast.error('Failed to fetch marketing leads');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const checkCallbacksToday = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get(`${API_BASE_URL}/MarketingLeads/callbacks-today/`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const callbacks = response.data.data;
            if (callbacks) {
                setTodayCallbacks(callbacks);
            }
        } catch (error) {
            console.error('Failed to check callbacks:', error);
        }
    };

    const toggleCallbackDone = async (lead) => {
        try {
            const token = localStorage.getItem('access_token');
            const updatedLead = { ...lead, callback_done: !lead.callback_done };
            await axios.put(`${API_BASE_URL}/MarketingLeads/${lead.id}/`, updatedLead, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(`Callback marked as ${updatedLead.callback_done ? 'done' : 'pending'}`);
            fetchLeads(currentPage);
            checkCallbacksToday();
        } catch (error) {
            toast.error('Failed to update callback status');
            console.error(error);
        }
    };

    const confirmDelete = (id) => {
        setLeadToDelete(id);
        setIsDeleteModalOpen(true);
    };

    const handleDelete = async () => {
        if (!leadToDelete) return;

        try {
            const token = localStorage.getItem('access_token');
            await axios.delete(`${API_BASE_URL}/MarketingLeads/${leadToDelete}/`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Lead deleted successfully');
            setIsDeleteModalOpen(false);
            setLeadToDelete(null);

            // If deleting the last item on a page (and not page 1), go back one page
            if (filteredLeads.length === 1 && currentPage > 1) {
                setCurrentPage(currentPage - 1);
            } else {
                fetchLeads(currentPage);
            }
        } catch (error) {
            toast.error('Failed to delete lead');
            console.error(error);
            setIsDeleteModalOpen(false);
            setLeadToDelete(null);
        }
    };

    const openModal = (lead = null) => {
        setEditingLead(lead);
        setIsModalOpen(true);
    };

    const getStatusColor = (status) => {
        const colors = {
            'Callback': 'bg-orange-100 text-orange-800',
            'Agreement Send': 'bg-blue-100 text-blue-800',
            'Onboard': 'bg-green-100 text-green-800',
            'Pending': 'bg-gray-100 text-gray-800',
            'Not Responding': 'bg-red-100 text-red-800',
            'Fresh': 'bg-purple-100 text-purple-800',
            'Not Interested': 'bg-rose-100 text-rose-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const handleDownloadSample = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get(`${API_BASE_URL}/MarketingLeads/sample-excel/`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'marketing_leads_sample.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            toast.error('Failed to download sample file');
            console.error('Error downloading sample:', error);
        }
    };

    const [failedRows, setFailedRows] = useState([]);
    const [isFailedRowsModalOpen, setIsFailedRowsModalOpen] = useState(false);

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        setLoading(true);
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.post(`${API_BASE_URL}/MarketingLeads/upload-excel/`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.failed_rows && response.data.failed_rows.length > 0) {
                setFailedRows(response.data.failed_rows);
                setIsFailedRowsModalOpen(true);
                toast.warning(`Uploaded ${response.data.success_count} leads. ${response.data.failed_count} rows failed.`);
            } else {
                toast.success(response.data.message || 'Leads uploaded successfully');
            }

            fetchLeads(1);
        } catch (error) {
            const errorMsg = error.response?.data?.error || 'Failed to upload file';
            if (error.response?.data?.details) {
                toast.error(`${errorMsg}: ${error.response.data.details[0]}`);
            } else if (error.response?.data?.failed_rows) {
                // If it's a 400 Bad Request but has failed_rows (meaning all rows failed)
                setFailedRows(error.response.data.failed_rows);
                setIsFailedRowsModalOpen(true);
                toast.error(`Upload failed. ${error.response.data.failed_count} rows had errors.`);
            } else {
                toast.error(errorMsg);
            }
            console.error('Upload error:', error.response?.data || error);
        } finally {
            setLoading(false);
            event.target.value = null; // Reset input
        }
    };


    return (
        <div style={{ display: 'flex', flexDirection: 'row', minHeight: '100vh' }}>
            <CustomNavbar />
            <ToastContainer />
            <div className="container mx-auto px-4 py-4">
                {/* Edit/Create Modal */}
                {isModalOpen && (
                    <MarketingLeadModal
                        lead={editingLead}
                        onClose={() => setIsModalOpen(false)}
                        onSuccess={() => {
                            setIsModalOpen(false);
                            fetchLeads(currentPage);
                        }}
                        apiBaseUrl={API_BASE_URL}
                    />
                )}

                {/* Delete Confirmation Modal */}
                {isDeleteModalOpen && (
                    <div className="fixed inset-0 flex justify-center items-center z-50">
                        <div className="absolute inset-0 bg-gray-800 bg-opacity-60 backdrop-blur-sm" onClick={() => setIsDeleteModalOpen(false)}></div>
                        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6 m-4 animate-fade-in-up">
                            <div className="text-center">
                                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2">Delete Lead</h3>
                                <div className="mt-2">
                                    <p className="text-sm text-gray-500">
                                        Are you sure you want to delete this lead? This action cannot be undone.
                                    </p>
                                </div>
                            </div>
                            <div className="mt-6 flex justify-center space-x-4">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsDeleteModalOpen(false);
                                        setLeadToDelete(null);
                                    }}
                                    className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-medium transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleDelete}
                                    className="px-4 py-2 bg-red-600 border border-transparent rounded-md text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 font-medium transition-colors"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Failed Rows Modal for Excel Upload */}
                {isFailedRowsModalOpen && (
                    <div className="fixed inset-0 flex justify-center items-center z-50">
                        <div className="absolute inset-0 bg-gray-800 bg-opacity-60 backdrop-blur-sm" onClick={() => setIsFailedRowsModalOpen(false)}></div>
                        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col m-4 animate-fade-in-up">
                            <div className="flex items-center justify-between p-5 border-b border-gray-100">
                                <div className="flex items-center gap-3">
                                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                        <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900">Upload Errors Dashboard</h3>
                                        <p className="text-sm text-gray-500 mt-0.5">{failedRows.length} row{failedRows.length !== 1 ? 's' : ''} failed to import.</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setIsFailedRowsModalOpen(false)}
                                    className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors"
                                >
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <div className="p-5 overflow-y-auto max-h-[60vh] flex-grow bg-slate-50">
                                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">Row</th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Hospital / Contact</th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Error Details</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {failedRows.map((row, index) => (
                                                <tr key={index} className="hover:bg-red-50/50 transition-colors">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-center bg-gray-50/50 border-r border-gray-100">
                                                        #{row.row_index}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="max-w-[200px] sm:max-w-xs">
                                                            <div className="text-sm font-semibold text-gray-900 truncate" title={row.hospital_name || 'N/A'}>
                                                                {row.hospital_name || <span className="text-gray-400 italic">No Name</span>}
                                                            </div>
                                                            <div className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                                                                <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                                </svg>
                                                                <span className="truncate">{row.contact_number || <span className="text-gray-400 italic">No Number</span>}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-red-600">
                                                        <ul className="list-disc pl-5 space-y-1">
                                                            {row.reason.split(' | ').map((error, idx) => (
                                                                <li key={idx}>
                                                                    {error.includes("Duplicate Contact Number") ? (
                                                                        <span>
                                                                            <strong className="font-semibold text-red-700">Duplicate:</strong> {error.replace("Duplicate Contact Number: ", "")}
                                                                        </span>
                                                                    ) : (
                                                                        error
                                                                    )}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="p-5 border-t border-gray-100 bg-gray-50 rounded-b-lg flex justify-end">
                                <button
                                    onClick={() => setIsFailedRowsModalOpen(false)}
                                    className="px-6 py-2.5 bg-white border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    Close Dashboard
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Header */}
                <div className="flex justify-between items-center mb-5">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800">Marketing Leads</h2>
                        <p className="text-sm mt-0.5">
                            {isShowingTodayCallbacks ? (
                                <span className="font-semibold text-red-600">Showing {todayCallbacks.length} pending callbacks</span>
                            ) : (
                                <span className="text-gray-500">{totalItems} total leads</span>
                            )}
                        </p>
                    </div>
                    <div className="flex items-center gap-4">
                        {todayCallbacks.length > 0 && (
                            <div
                                className={`group relative cursor-pointer px-4 py-2 rounded-lg border transition-all duration-200 ${isShowingTodayCallbacks ? 'bg-red-50 border-red-300 shadow-inner' : 'bg-white border-red-200 shadow-sm hover:shadow hover:border-red-300'}`}
                                onClick={() => {
                                    if (!isShowingTodayCallbacks) {
                                        setIsShowingTodayCallbacks(true);
                                        setFilteredLeads(todayCallbacks);
                                        setTotalItems(todayCallbacks.length);
                                        setTotalPages(1);
                                    } else {
                                        fetchLeads(1);
                                    }
                                }}
                            >
                                <div className="flex items-center gap-2">
                                    <span className="relative flex h-3 w-3">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                                    </span>
                                    <span className={`font-bold text-sm ${isShowingTodayCallbacks ? 'text-red-700' : 'text-red-600'}`}>
                                        {todayCallbacks.length} Pending Callback{todayCallbacks.length !== 1 ? 's' : ''}
                                    </span>
                                </div>
                                {/* Hover Tooltip */}
                                <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 p-2 text-left">
                                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1 px-2 pt-1 border-b pb-2 text-left">Remaining tasks</p>
                                    <div className="max-h-48 overflow-y-auto">
                                        {todayCallbacks.map(cb => (
                                            <div key={cb.id} className="text-sm text-gray-700 px-2 py-1.5 hover:bg-gray-50 rounded truncate font-medium flex justify-between items-center">
                                                <span>{cb.hospital_name}</span>
                                                <span className="text-[10px] bg-orange-50 text-orange-600 px-1.5 py-0.5 rounded border border-orange-100 uppercase tracking-tighter">
                                                    {new Date(cb.callback_date).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                    <p className="text-xs text-gray-400 mt-2 px-2 italic text-left">Click badge to focus on these</p>
                                </div>
                            </div>
                        )}
                        {canEdit && (
                            <div className="flex items-center gap-2 flex-wrap justify-end">

                                <label className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded-lg shadow-sm transition duration-200 cursor-pointer">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                    </svg>
                                    Upload Data
                                    <input
                                        type="file"
                                        accept=".xlsx, .xls, .csv"
                                        onChange={handleFileUpload}
                                        className="hidden"
                                    />
                                </label>
                                <button
                                    onClick={() => openModal()}
                                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg shadow-sm transition duration-200"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                    Add New Lead
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Superadmin Stats Panel */}
                {isSuperuser && stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                        {[
                            { label: 'Last Month', value: stats.last_1_month, color: 'blue' },
                            { label: 'Last 3 Months', value: stats.last_3_months, color: 'indigo' },
                            { label: 'Last 6 Months', value: stats.last_6_months, color: 'violet' },
                            { label: 'All Time', value: stats.all_time, color: 'gray' },
                        ].map(({ label, value, color }) => (
                            <div key={label} className={`bg-white border border-${color}-100 rounded-xl shadow-sm p-4 flex flex-col items-center`}>
                                <span className={`text-2xl font-extrabold text-${color}-600`}>{value}</span>
                                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mt-1">{label}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Filters */}
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-5">
                    <div className="flex items-center gap-2 mb-3">
                        <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                        </svg>
                        <span className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Filters</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Phone</label>
                            <input
                                type="text"
                                value={searchNumber}
                                onChange={(e) => { setSearchNumber(e.target.value); setCurrentPage(1); }}
                                placeholder="Search..."
                                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-400"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Hospital</label>
                            <input
                                type="text"
                                value={searchHospital}
                                onChange={(e) => { setSearchHospital(e.target.value); setCurrentPage(1); }}
                                placeholder="Search..."
                                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-400"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">City</label>
                            <input
                                type="text"
                                value={searchCity}
                                onChange={(e) => { setSearchCity(e.target.value); setCurrentPage(1); }}
                                placeholder="Search..."
                                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-400"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">State</label>
                            <input
                                type="text"
                                value={searchState}
                                onChange={(e) => { setSearchState(e.target.value); setCurrentPage(1); }}
                                placeholder="Search..."
                                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-400"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Created By</label>
                            <select
                                value={searchCreatedBy}
                                onChange={(e) => { setSearchCreatedBy(e.target.value); setCurrentPage(1); }}
                                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-400"
                            >
                                <option value="">All</option>
                                {creators.map((name) => (
                                    <option key={name} value={name}>{name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Source</label>
                            <select
                                value={searchSource}
                                onChange={(e) => { setSearchSource(e.target.value); setCurrentPage(1); }}
                                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-400"
                            >
                                <option value="">All</option>
                                <option value="SEO">SEO</option>
                                <option value="References">References</option>
                                <option value="Internal reference">Internal</option>
                                <option value="Social media">Social</option>
                                <option value="Ops Team">Ops Team</option>
                                <option value="Default">Default</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                            <select
                                value={statusFilter}
                                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-400"
                            >
                                <option value="">All</option>
                                <option value="Callback">Callback</option>
                                <option value="Agreement Send">Agreement Sent</option>
                                <option value="Onboard">Onboard</option>
                                <option value="Pending">Pending</option>
                                <option value="Not Responding">Not Responding</option>
                                <option value="Fresh">Fresh</option>
                                <option value="Not Interested">Not Interested</option>
                                <option value="Default">Default</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Callback Status</label>
                            <select
                                value={callbackDoneFilter}
                                onChange={(e) => { setCallbackDoneFilter(e.target.value); setCurrentPage(1); }}
                                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-400"
                            >
                                <option value="">All</option>
                                <option value="false">Pending</option>
                                <option value="true">Done</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Followup By</label>
                            <MultiSelectDropdown
                                options={[
                                    { value: 'Komal', label: 'Komal' },
                                    { value: 'Anju', label: 'Anju' }
                                ]}
                                selectedValues={followupByFilter}
                                onChange={(value) => { setFollowupByFilter(value); setCurrentPage(1); }}
                                placeholder="All"
                                className="w-full text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Quality</label>
                            <select
                                value={qualityFilter}
                                onChange={(e) => { setQualityFilter(e.target.value); setCurrentPage(1); }}
                                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-400"
                            >
                                <option value="">All</option>
                                <option value="Relevant">Relevant</option>
                                <option value="Irrelevant">Irrelevant</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Reason</label>
                            <select
                                value={searchReason}
                                onChange={(e) => { setSearchReason(e.target.value); setCurrentPage(1); }}
                                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-400"
                            >
                                <option value="">All</option>
                                <option value="Charges issue">Charges issue</option>
                                <option value="Salary not match">Salary not match</option>
                                <option value="Terms & Conditions not match">Terms &amp; Conditions not match</option>
                                <option value="Any other">Any other</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Date From</label>
                            <input
                                type="date"
                                value={dateFrom}
                                onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
                                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-400"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Date To</label>
                            <input
                                type="date"
                                value={dateTo}
                                onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
                                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-400"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Leads Per Page</label>
                            <select
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-400 font-medium"
                            >
                                <option value={25}>25 leads</option>
                                <option value={50}>50 leads</option>
                                <option value={100}>100 leads</option>
                                <option value={200}>200 leads</option>
                                <option value={500}>500 leads</option>
                            </select>
                        </div>
                        <div className="flex items-end">
                            <button
                                onClick={() => {
                                    setSearchNumber('');
                                    setStatusFilter('');
                                    setSearchHospital('');
                                    setSearchSource('');
                                    setSearchCity('');
                                    setSearchState('');
                                    setSearchCreatedBy('');
                                    setQualityFilter('');
                                    setSearchReason('');
                                    setDateFrom('');
                                    setDateTo('');
                                    setCallbackDoneFilter('');
                                    setFollowupByFilter('');
                                    setPageSize(100);
                                    setCurrentPage(1);
                                }}
                                className="w-full px-2 py-1.5 text-sm bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition-colors font-medium"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

                {/* Table */}
                {loading ? (
                    <div className="animate-pulse">
                        <div className="bg-white shadow-md rounded-lg overflow-hidden">
                            <div className="h-10 bg-blue-200"></div>
                            {[1, 2, 3, 4, 5].map(i => (
                                <div key={i} className="flex gap-4 px-3 py-3 border-b">
                                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                                    <div className="h-4 bg-gray-200 rounded w-40"></div>
                                    <div className="h-4 bg-gray-200 rounded w-28"></div>
                                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : filteredLeads.length === 0 ? (
                    <div className="bg-white border border-gray-200 p-12 rounded-xl text-center shadow-sm">
                        <svg className="mx-auto h-12 w-12 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        <p className="text-gray-500 font-medium">No leads found</p>
                        {canEdit && <p className="text-sm text-gray-400 mt-1">Click "Add New Lead" to create one.</p>}
                    </div>
                ) : (
                    <div className="bg-white shadow-md rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-blue-600 border-b">
                                        <th className="py-3 px-3 text-left text-white text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Date</th>
                                        <th className="py-3 px-3 text-left text-white text-xs font-semibold uppercase tracking-wider">Hospital Name</th>
                                        <th className="py-3 px-3 text-left text-white text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Contact Person</th>
                                        <th className="py-3 px-3 text-left text-white text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Phone</th>
                                        <th className="py-3 px-3 text-left text-white text-xs font-semibold uppercase tracking-wider">City / State</th>
                                        <th className="py-3 px-3 text-left text-white text-xs font-semibold uppercase tracking-wider">Source</th>
                                        <th className="py-3 px-3 text-left text-white text-xs font-semibold uppercase tracking-wider">Followup</th>
                                        <th className="py-3 px-3 text-center text-white text-xs font-semibold uppercase tracking-wider">Status</th>
                                        <th className="py-3 px-3 text-center text-white text-xs font-semibold uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredLeads.map((lead) => (
                                        <React.Fragment key={lead.id}>
                                            <tr className="border-b last:border-b-0 hover:bg-blue-50 transition-colors duration-150">
                                                <td className="py-3 px-3 text-sm text-gray-500 whitespace-nowrap">{new Date(lead.date).toLocaleDateString()}</td>
                                                <td className="py-3 px-3">
                                                    <span className="font-semibold text-gray-800 text-sm">{lead.hospital_name}</span>
                                                </td>
                                                <td className="py-3 px-3 text-sm text-gray-700">{lead.contact_person}</td>
                                                <td className="py-3 px-3 text-sm text-gray-700 whitespace-nowrap">{lead.contact_number}</td>
                                                <td className="py-3 px-3">
                                                    <span className="text-sm text-gray-700">{lead.city}</span>
                                                    <span className="block text-xs text-gray-400">{lead.state}</span>
                                                </td>
                                                <td className="py-3 px-3 text-sm text-gray-600">{lead.source || <span className="text-gray-300">—</span>}</td>
                                                <td className="py-3 px-3 text-sm text-gray-700">
                                                    {lead.followup_by ? (
                                                        <span className="font-medium text-blue-600">{lead.followup_by}</span>
                                                    ) : (
                                                        <span className="text-gray-300">—</span>
                                                    )}
                                                </td>
                                                <td className="py-3 px-3 text-center">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getStatusColor(lead.status)}`}>
                                                        {lead.status}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-3">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <button
                                                            onClick={() => setExpandedLeadId(expandedLeadId === lead.id ? null : lead.id)}
                                                            className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md transition-colors ${expandedLeadId === lead.id
                                                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                                }`}
                                                        >
                                                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={expandedLeadId === lead.id ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                                                            </svg>
                                                            {expandedLeadId === lead.id ? 'Hide' : 'Details'}
                                                        </button>
                                                        {canEdit && (
                                                            <>
                                                                <button
                                                                    onClick={() => openModal(lead)}
                                                                    className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                                                                >
                                                                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                    </svg>
                                                                    Edit
                                                                </button>
                                                                <button
                                                                    onClick={() => confirmDelete(lead.id)}
                                                                    className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                                                                >
                                                                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                    </svg>
                                                                    Del
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                            {expandedLeadId === lead.id && (
                                                <tr>
                                                    <td colSpan="9" className="p-0">
                                                        <div style={{ borderLeft: '4px solid #3b82f6', background: 'linear-gradient(to bottom right, #eff6ff, #f8fafc)' }} className="p-5">
                                                            {/* Header bar */}
                                                            <div className="flex items-center justify-between mb-4">
                                                                <div className="flex items-center gap-2">
                                                                    <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                    </svg>
                                                                    <span className="text-sm font-semibold text-blue-700 uppercase tracking-wider">Lead Details — {lead.hospital_name}</span>
                                                                </div>
                                                                <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(lead.status)}`}>{lead.status}</span>
                                                            </div>

                                                            {/* Detail cards grid */}
                                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                                                                <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
                                                                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">📍 State</p>
                                                                    <p className="text-sm text-gray-800 font-medium">{lead.state}</p>
                                                                </div>

                                                                <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
                                                                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">👤 Created By</p>
                                                                    <p className="text-sm text-gray-800 font-medium">{lead.created_by}</p>
                                                                </div>

                                                                <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
                                                                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">📞 Followup By</p>
                                                                    <p className="text-sm text-gray-800 font-medium">{lead.followup_by || <span className="italic text-gray-400">Not assigned</span>}</p>
                                                                </div>

                                                                {lead.callback_date && (() => {
                                                                    const today = new Date();
                                                                    const cb = new Date(lead.callback_date);
                                                                    const isToday =
                                                                        cb.getFullYear() === today.getFullYear() &&
                                                                        cb.getMonth() === today.getMonth() &&
                                                                        cb.getDate() === today.getDate();

                                                                    const isOverdue = !lead.callback_done && cb < today && !isToday;

                                                                    return (
                                                                        <div className={`relative rounded-lg p-3 shadow-sm border transition-all ${lead.callback_done ? 'bg-gray-50 border-gray-200 opacity-75' : isToday ? 'bg-red-50 border-red-300 ring-1 ring-red-100' : isOverdue ? 'bg-amber-50 border-amber-200' : 'bg-orange-50 border-orange-100'}`}>
                                                                            <div className="flex justify-between items-start mb-1">
                                                                                <p className={`text-xs font-semibold uppercase tracking-wider ${lead.callback_done ? 'text-gray-400' : isToday ? 'text-red-500' : isOverdue ? 'text-amber-600' : 'text-orange-400'}`}>
                                                                                    {lead.callback_done ? '✔️ Callback Done' : isToday ? '🔴 Callback Today!' : isOverdue ? '⚠️ Overdue Callback' : '📅 Callback Date'}
                                                                                </p>
                                                                                {canEdit && (
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={lead.callback_done || false}
                                                                                        onChange={() => toggleCallbackDone(lead)}
                                                                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                                                                                        title="Mark as done"
                                                                                    />
                                                                                )}
                                                                            </div>
                                                                            <p className={`text-sm font-bold ${lead.callback_done ? 'text-gray-500 line-through' : isToday ? 'text-red-700' : isOverdue ? 'text-amber-700' : 'text-orange-700'}`}>
                                                                                {new Date(lead.callback_date).toLocaleDateString()}
                                                                            </p>
                                                                            {lead.callback_done && (
                                                                                <p className="text-[10px] text-gray-400 mt-1 italic">Task completed</p>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })()}

                                                                <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
                                                                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">🏷️ Source</p>
                                                                    <p className="text-sm text-gray-800 font-medium">{lead.source || <span className="italic text-gray-400">Not specified</span>}</p>
                                                                </div>

                                                                <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
                                                                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">💰 Consult Charges</p>
                                                                    <p className="text-sm text-gray-800 font-medium">{lead.consultation_charges ? `₹${lead.consultation_charges}` : <span className="italic text-gray-400">N/A</span>}</p>
                                                                </div>

                                                                <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
                                                                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">🛏️ Beds</p>
                                                                    <p className="text-sm text-gray-800 font-medium">{lead.bedded || <span className="italic text-gray-400">N/A</span>}</p>
                                                                </div>

                                                                <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
                                                                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">⭐ Quality</p>
                                                                    <p className="text-sm text-gray-800 font-medium">
                                                                        {lead.quality ? (
                                                                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${lead.quality === 'Relevant' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                                                {lead.quality}
                                                                            </span>
                                                                        ) : <span className="italic text-gray-400">N/A</span>}
                                                                    </p>
                                                                </div>

                                                                {lead.reason && (
                                                                    <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100 md:col-span-full">
                                                                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">📝 Reason</p>
                                                                        <p className="text-sm text-gray-800 font-medium">{lead.reason}</p>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Wide area fields */}
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                                                                <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
                                                                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">📋 Requirement</p>
                                                                    <p className="text-sm text-gray-700 leading-relaxed">{lead.requirement}</p>
                                                                </div>
                                                                <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
                                                                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">💬 Remarks</p>
                                                                    <p className="text-sm text-gray-700 leading-relaxed">{lead.remarks || <span className="italic text-gray-400">No remarks added</span>}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>

                            {/* Pagination Controls */}
                            {totalItems > 0 && (
                                <div className="flex flex-col sm:flex-row justify-between items-center gap-3 bg-gray-50 p-4 border-t border-gray-100 rounded-b-lg">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <div className="text-sm text-gray-500 font-medium">
                                            Showing {filteredLeads.length > 0 ? ((currentPage - 1) * pageSize) + 1 : 0} to {Math.min(currentPage * pageSize, totalItems)} of {totalItems} leads
                                        </div>
                                        <div className="flex items-center gap-1.5 text-sm text-gray-600 border-l border-gray-200 pl-3">
                                            <span className="text-xs text-gray-500 font-medium">Per page:</span>
                                            <select
                                                value={pageSize}
                                                onChange={(e) => {
                                                    setPageSize(Number(e.target.value));
                                                    setCurrentPage(1);
                                                }}
                                                className="px-2 py-1 text-xs border border-gray-300 rounded bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                            >
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                                <option value={200}>200</option>
                                                <option value={500}>500</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1.5 text-sm font-medium border border-gray-200 rounded-md disabled:bg-gray-50 disabled:text-gray-400 hover:bg-gray-100 bg-white transition-colors"
                                        >
                                            Previous
                                        </button>
                                        <span className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-1.5 text-sm font-medium border border-gray-200 rounded-md disabled:bg-gray-50 disabled:text-gray-400 hover:bg-gray-100 bg-white transition-colors"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// Modal Component
const MarketingLeadModal = ({ lead, onClose, onSuccess, apiBaseUrl }) => {
    const [formData, setFormData] = useState({
        date: '',
        hospital_name: '',
        contact_person: '',
        contact_number: '',
        city: '',
        state: '',
        requirement: '',
        remarks: '',
        status: 'Pending',
        source: '',
        callback_date: '',
        consultation_charges: '',
        bedded: '',
        quality: '',
        reason: '',
        callback_done: false,
        followup_by: '',
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (lead) {
            setFormData(lead);
        }
    }, [lead]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const token = localStorage.getItem('access_token');

            // Prepare form data - set callback_date to null if status is not Callback
            const submitData = { ...formData };
            if (submitData.status !== 'Callback') {
                submitData.callback_date = null;
            }

            if (lead) {
                await axios.put(
                    `${apiBaseUrl}/MarketingLeads/${lead.id}/`,
                    submitData,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success('Lead updated successfully');
            } else {
                await axios.post(
                    `${apiBaseUrl}/MarketingLeads/`,
                    submitData,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success('Lead created successfully');
            }
            onSuccess();
        } catch (error) {
            console.error('Error saving lead:', error);

            // Handle network/CORS errors
            if (!error.response) {
                if (error.message && error.message.includes('Network Error')) {
                    toast.error('Network error: Unable to connect to server. Please check your connection.');
                } else {
                    toast.error('Failed to save lead: ' + (error.message || 'Unknown error'));
                }
                setLoading(false);
                return;
            }

            // Handle API validation errors
            if (error.response?.data) {
                const errors = error.response.data;
                Object.keys(errors).forEach(key => {
                    const errorMsg = Array.isArray(errors[key]) ? errors[key].join(', ') : errors[key];
                    toast.error(`${key}: ${errorMsg}`);
                });
            } else {
                toast.error('Failed to save lead');
            }
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 flex justify-center items-center z-50" style={{ width: "100vw", height: "100vh" }}>
            <div className="absolute inset-0 bg-gray-800 bg-opacity-60 backdrop-blur-sm" onClick={onClose}></div>
            <div className="relative bg-white rounded-lg shadow-lg" style={{ height: "95vh", overflowY: "auto", width: "60vw" }}>
                <div className="overflow-auto" style={{ padding: "40px" }}>
                    <h2 className="text-center text-blue-700 mb-5 text-2xl font-bold">
                        {lead ? 'Edit Marketing Lead' : 'Add New Marketing Lead'}
                    </h2>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                            <input
                                type="date"
                                name="date"
                                value={formData.date}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Hospital Name</label>
                            <input
                                type="text"
                                name="hospital_name"
                                value={formData.hospital_name}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Person</label>
                                <input
                                    type="text"
                                    name="contact_person"
                                    value={formData.contact_person}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number</label>
                                <input
                                    type="text"
                                    name="contact_number"
                                    value={formData.contact_number}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                                <input
                                    type="text"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                                <input
                                    type="text"
                                    name="state"
                                    value={formData.state}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Requirement</label>
                            <textarea
                                name="requirement"
                                value={formData.requirement}
                                onChange={handleChange}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Remarks</label>
                            <textarea
                                name="remarks"
                                value={formData.remarks}
                                onChange={handleChange}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Source</label>
                                <select
                                    name="source"
                                    value={formData.source || ''}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">Select Source</option>
                                    <option value="SEO">SEO</option>
                                    <option value="References">References</option>
                                    <option value="Internal reference">Internal reference</option>
                                    <option value="Social media">Social media</option>
                                    <option value="Ops Team">Ops Team</option>
                                    <option value="Default">Default</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Quality</label>
                                <select
                                    name="quality"
                                    value={formData.quality || ''}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">Select Quality</option>
                                    <option value="Relevant">Relevant</option>
                                    <option value="Irrelevant">Irrelevant</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Followup By</label>
                                <MultiSelectDropdown
                                    options={[
                                        { value: 'Komal', label: 'Komal' },
                                        { value: 'Anju', label: 'Anju' }
                                    ]}
                                    selectedValues={formData.followup_by || ''}
                                    onChange={(value) => handleChange({ target: { name: 'followup_by', value: value }})}
                                    placeholder="Select Followup By"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Consultation Charges</label>
                                <input
                                    type="text"
                                    name="consultation_charges"
                                    value={formData.consultation_charges}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Bedded</label>
                                <input
                                    type="text"
                                    name="bedded"
                                    value={formData.bedded}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Reason (If Irrelevant)</label>
                            <select
                                name="reason"
                                value={formData.reason || ''}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">Select Reason</option>
                                <option value="Charges issue">Charges issue</option>
                                <option value="Salary not match">Salary not match</option>
                                <option value="Terms & Conditions not match">Terms &amp; Conditions not match</option>
                                <option value="Any other">Any other</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Status *</label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="Callback">Callback</option>
                                <option value="Agreement Send">Agreement Send</option>
                                <option value="Onboard">Onboard</option>
                                <option value="Pending">Pending</option>
                                <option value="Not Responding">Not Responding</option>
                                <option value="Fresh">Fresh</option>
                                <option value="Not Interested">Not Interested</option>
                                <option value="Default">Default</option>
                            </select>
                        </div>
                        {formData.status === 'Callback' && (
                            <div className="grid grid-cols-2 gap-4 items-end">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Callback Date *</label>
                                    <input
                                        type="date"
                                        name="callback_date"
                                        value={formData.callback_date}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div className="flex items-center mb-3">
                                    <input
                                        type="checkbox"
                                        id="callback_done"
                                        name="callback_done"
                                        checked={formData.callback_done}
                                        onChange={handleChange}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                                    />
                                    <label htmlFor="callback_done" className="ml-2 block text-sm text-gray-700 font-medium cursor-pointer">
                                        Callback Done
                                    </label>
                                </div>
                            </div>
                        )}
                        <div className="flex justify-end space-x-4 mt-6">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
                            >
                                {loading ? 'Saving...' : (lead ? 'Update' : 'Create')}
                            </button>
                        </div>
                    </form>
                </div>
            </div >
        </div >
    );
};

export default MarketingLeads;
